import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';
import CompanyBrandMark from './CompanyBrandMark';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();
  const { t } = useLanguage();

  const footerSections = [
    {
      titleKey: 'footer.services',
      links: [
        { labelKey: 'footer.hrSolutions', path: '/service-portfolio-hub' },
        { labelKey: 'footer.jobMarketplace', path: '/job-marketplace' },
        { labelKey: 'footer.tradeNetwork', path: '/global-trade-network' },
        { labelKey: 'footer.engineeringServices', path: '/service-portfolio-hub' },
      ],
    },
    {
      titleKey: 'footer.company',
      links: [
        { labelKey: 'footer.aboutUs', path: '/about' },
        { labelKey: 'footer.successStories', path: '/success-stories-showcase' },
        { labelKey: 'footer.clientPortal', path: '/client-portal' },
        { labelKey: 'footer.contact', path: '/contact' },
      ],
    },
    {
      titleKey: 'footer.resources',
      links: [
        { labelKey: 'footer.blog', path: '/blog' },
        { labelKey: 'footer.caseStudies', path: '/case-studies' },
        { labelKey: 'footer.marketReports', path: '/resources' },
        { labelKey: 'footer.careerGuide', path: '/career-guide' },
      ],
    },
    {
      titleKey: 'footer.legal',
      links: [
        { labelKey: 'footer.privacyPolicy', path: '/privacy' },
        { labelKey: 'footer.termsOfService', path: '/terms' },
        { labelKey: 'footer.cookiePolicy', path: '/cookies' },
        { labelKey: 'footer.compliance', path: '/compliance' },
      ],
    },
  ];

  const socialLinks = [
    { name: 'Linkedin', icon: 'Linkedin', url: 'https://linkedin.com' },
    { name: 'Twitter', icon: 'Twitter', url: 'https://twitter.com' },
    { name: 'Facebook', icon: 'Facebook', url: 'https://facebook.com' },
    { name: 'Instagram', icon: 'Instagram', url: 'https://instagram.com' },
  ];

  return (
    <footer className="bg-brand-primary text-white mt-auto">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-8">
          <div className="lg:col-span-2">
            <Link to="/homepage" className="inline-flex items-center mb-4">
              <CompanyBrandMark
                size="md"
                onDark
                nameClassName="text-xl font-bold font-heading text-white"
                iconWrapperClassName="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center text-white"
              />
            </Link>
            <p className="text-sm text-gray-400 mb-4 max-w-sm">{t('footer.tagline')}</p>
            <div className="flex gap-3">
              {socialLinks?.map((social) => (
                <a
                  key={social?.name}
                  href={social?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-md bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200"
                  aria-label={social?.name}
                >
                  <Icon name={social?.icon} size={18} />
                </a>
              ))}
            </div>
          </div>

          {footerSections?.map((section) => (
            <div key={section?.titleKey}>
              <h3 className="font-semibold font-heading mb-4 text-sm uppercase tracking-wider">
                {t(section.titleKey)}
              </h3>
              <ul className="space-y-2">
                {section?.links?.map((link) => (
                  <li key={link?.labelKey}>
                    <Link
                      to={link?.path}
                      className="text-sm text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      {t(link.labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
              <span>{t('footer.copyright', { year: currentYear })}</span>
              <span className="hidden md:inline">|</span>
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={16} />
                <span>{t('footer.location')}</span>
              </div>
              <span className="hidden md:inline">|</span>
              <div className="flex items-center gap-2">
                <Icon name="Mail" size={16} />
                <a
                  href="mailto:info@nchisolutions.com"
                  className="hover:text-white transition-colors"
                >
                  info@nchisolutions.com
                </a>
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Icon name="Award" size={16} />
                <span>{t('footer.isoCertified')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
